

































































































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import ngeohash, { GeographicPoint } from "ngeohash";
import {
  endOfToday,
  addDays,
  startOfDay,
  endOfDay,
  endOfWeek,
  addWeeks,
  startOfWeek,
  endOfMonth,
  addMonths,
  startOfMonth,
  eachWeekendOfInterval,
} from "date-fns";
import { SearchTabs } from "@/types/enums";
import MultiCard from "../components/Event/MultiCard.vue";
import { FETCH_EVENTS } from "../graphql/event";
import { EventType, IEvent } from "../types/event.model";
import RouteName from "../router/name";
import { IAddress, Address } from "../types/address.model";
import FullAddressAutoComplete from "../components/Event/FullAddressAutoComplete.vue";
import { SEARCH_EVENTS_AND_GROUPS } from "../graphql/search";
import { Paginate } from "../types/paginate";
import { IGroup } from "../types/actor";
import MultiGroupCard from "../components/Group/MultiGroupCard.vue";
import { CONFIG } from "../graphql/config";
import { REVERSE_GEOCODE } from "../graphql/address";
import debounce from "lodash/debounce";
import { CURRENT_USER_CLIENT } from "@/graphql/user";
import { ICurrentUser } from "@/types/current-user.model";

interface ISearchTimeOption {
  label: string;
  start?: Date | null;
  end?: Date | null;
}

const EVENT_PAGE_LIMIT = 12;

const GROUP_PAGE_LIMIT = 12;

const DEFAULT_RADIUS = 25; // value to set if radius is null but location set

const DEFAULT_ZOOM = 11; // zoom on a city

const GEOHASH_DEPTH = 9; // put enough accuracy, radius will be used anyway

@Component({
  components: {
    MultiCard,
    FullAddressAutoComplete,
    MultiGroupCard,
  },
  apollo: {
    config: CONFIG,
    events: {
      query: FETCH_EVENTS,
      variables() {
        return {
          page: this.featuredEventPage,
          limit: EVENT_PAGE_LIMIT,
        };
      },
    },
    searchElements: {
      query: SEARCH_EVENTS_AND_GROUPS,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          term: this.search,
          tags: this.tag,
          location: this.geohash,
          beginsOn: this.start,
          endsOn: this.end,
          radius: this.radius,
          eventPage: this.eventPage,
          groupPage: this.groupPage,
          limit: EVENT_PAGE_LIMIT,
          type: this.type,
        };
      },
      update(data) {
        this.searchEvents = data.searchEvents;
        this.searchGroups = data.searchGroups;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.$refs.autocompleteSearchInput?.focus();
      },
    },
    currentUser: CURRENT_USER_CLIENT,
  },
  metaInfo() {
    return {
      title: this.$t("Explore events") as string,
      titleTemplate: "%s | Mobilizon",
    };
  },
})
export default class Search extends Vue {
  @Prop({ type: String, required: false }) tag!: string;

  events: Paginate<IEvent> = {
    total: 0,
    elements: [],
  };

  searchEvents: Paginate<IEvent> = {
    total: 0,
    elements: [],
  };

  searchGroups: Paginate<IGroup> = { total: 0, elements: [] };

  location: IAddress = new Address();

  currentUser!: ICurrentUser;

  dateOptions: Record<string, ISearchTimeOption> = {
    past: {
      label: this.$t("In the past") as string,
      start: null,
      end: new Date(),
    },
    today: {
      label: this.$t("Today") as string,
      start: new Date(),
      end: endOfToday(),
    },
    tomorrow: {
      label: this.$t("Tomorrow") as string,
      start: startOfDay(addDays(new Date(), 1)),
      end: endOfDay(addDays(new Date(), 1)),
    },
    weekend: {
      label: this.$t("This weekend") as string,
      start: this.weekend.start,
      end: this.weekend.end,
    },
    week: {
      label: this.$t("This week") as string,
      start: new Date(),
      end: endOfWeek(new Date(), { locale: this.$dateFnsLocale }),
    },
    next_week: {
      label: this.$t("Next week") as string,
      start: startOfWeek(addWeeks(new Date(), 1), {
        locale: this.$dateFnsLocale,
      }),
      end: endOfWeek(addWeeks(new Date(), 1), { locale: this.$dateFnsLocale }),
    },
    month: {
      label: this.$t("This month") as string,
      start: new Date(),
      end: endOfMonth(new Date()),
    },
    next_month: {
      label: this.$t("Next month") as string,
      start: startOfMonth(addMonths(new Date(), 1)),
      end: endOfMonth(addMonths(new Date(), 1)),
    },
    any: {
      label: this.$t("Any day") as string,
      start: undefined,
      end: undefined,
    },
  };

  EVENT_PAGE_LIMIT = EVENT_PAGE_LIMIT;

  GROUP_PAGE_LIMIT = GROUP_PAGE_LIMIT;

  $refs!: {
    aac: FullAddressAutoComplete;
    autocompleteSearchInput: any;
  };

  data(): Record<string, unknown> {
    return {
      debouncedUpdateSearchQuery: debounce(this.updateSearchQuery, 500),
    };
  }

  mounted(): void {
    this.prepareLocation(this.$route.query.geohash as string);
  }

  radiusString = (radius: number | null): string => {
    if (radius) {
      return this.$tc("{nb} km", radius, { nb: radius }) as string;
    }
    return this.$t("any distance") as string;
  };

  radiusOptions: (number | null)[] = [1, 5, 10, 25, 50, 100, 150, null];

  submit(): void {
    this.$apollo.queries.searchEvents.refetch();
  }

  updateSearchQuery(searchQuery: string): void {
    this.search = searchQuery;
  }

  get featuredEventPage(): number {
    return parseInt(this.$route.query.featuredEventPage as string, 10) || 1;
  }

  set featuredEventPage(page: number) {
    this.$router.push({
      name: this.$route.name || RouteName.SEARCH,
      query: { ...this.$route.query, featuredEventPage: page.toString() },
    });
  }

  get eventPage(): number {
    return parseInt(this.$route.query.eventPage as string, 10) || 1;
  }

  set eventPage(page: number) {
    this.$router.push({
      name: this.$route.name || RouteName.SEARCH,
      query: { ...this.$route.query, eventPage: page.toString() },
    });
  }

  get groupPage(): number {
    return parseInt(this.$route.query.groupPage as string, 10) || 1;
  }

  set groupPage(page: number) {
    this.$router.push({
      name: this.$route.name || RouteName.SEARCH,
      query: { ...this.$route.query, groupPage: page.toString() },
    });
  }

  get search(): string | undefined {
    return this.$route.query.term as string;
  }

  set search(term: string | undefined) {
    this.$router.replace({
      name: RouteName.SEARCH,
      query: { ...this.$route.query, term },
    });
  }

  get activeTab(): SearchTabs {
    return (
      parseInt(this.$route.query.searchType as string, 10) || SearchTabs.EVENTS
    );
  }

  set activeTab(value: SearchTabs) {
    this.$router.replace({
      name: RouteName.SEARCH,
      query: { ...this.$route.query, searchType: value.toString() },
    });
  }

  get geohash(): string | undefined {
    if (this.location?.geom) {
      const [lon, lat] = this.location.geom.split(";");
      return ngeohash.encode(lat, lon, GEOHASH_DEPTH);
    }
    return undefined;
  }

  set geohash(value: string | undefined) {
    this.$router.replace({
      name: RouteName.SEARCH,
      query: { ...this.$route.query, geohash: value },
    });
  }

  get radius(): number | null {
    if (this.$route.query.radius === "any") {
      return null;
    }
    return parseInt(this.$route.query.radius as string, 10) || null;
  }

  set radius(value: number | null) {
    const radius = value === null ? "any" : value.toString();
    this.$router.replace({
      name: RouteName.SEARCH,
      query: { ...this.$route.query, radius },
    });
  }

  get when(): string {
    return (this.$route.query.when as string) || "any";
  }

  set when(value: string) {
    this.$router.replace({
      name: RouteName.SEARCH,
      query: { ...this.$route.query, when: value },
    });
  }

  get type(): EventType {
    return this.$route.query.type as EventType;
  }

  set type(type: EventType) {
    const query = { ...this.$route.query, type };
    if (type == null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete query.type;
    }
    this.$router.replace({
      name: RouteName.SEARCH,
      query,
    });
  }

  get weekend(): { start: Date; end: Date } {
    const now = new Date();
    const endOfWeekDate = endOfWeek(now, { locale: this.$dateFnsLocale });
    const startOfWeekDate = startOfWeek(now, { locale: this.$dateFnsLocale });
    const [start, end] = eachWeekendOfInterval({
      start: startOfWeekDate,
      end: endOfWeekDate,
    });
    return { start: startOfDay(start), end: endOfDay(end) };
  }

  private prepareLocation(value: string | undefined): void {
    if (value !== undefined) {
      // decode
      const latlon = ngeohash.decode(value);
      // set location
      this.reverseGeoCode(latlon, DEFAULT_ZOOM);
    }
  }

  async reverseGeoCode(e: GeographicPoint, zoom: number): Promise<void> {
    const result = await this.$apollo.query({
      query: REVERSE_GEOCODE,
      variables: {
        latitude: e.latitude,
        longitude: e.longitude,
        zoom,
        locale: this.$i18n.locale,
      },
    });
    const addressData = result.data.reverseGeocode.map(
      (address: IAddress) => new Address(address)
    );
    if (addressData.length > 0) {
      this.location = addressData[0];
    }
  }

  locchange = (e: IAddress): void => {
    if (this.radius === undefined || this.radius === null) {
      this.radius = DEFAULT_RADIUS;
    }
    if (e?.geom) {
      const [lon, lat] = e.geom.split(";");
      this.geohash = ngeohash.encode(lat, lon, GEOHASH_DEPTH);
    } else {
      this.geohash = undefined;
    }
  };

  get start(): Date | undefined | null {
    if (this.dateOptions[this.when]) {
      return this.dateOptions[this.when].start;
    }
    return undefined;
  }

  get end(): Date | undefined | null {
    if (this.dateOptions[this.when]) {
      return this.dateOptions[this.when].end;
    }
    return undefined;
  }

  get canSearchGroups(): boolean {
    return (
      this.stringExists(this.search) ||
      (this.stringExists(this.geohash) && this.valueExists(this.radius))
    );
  }

  get canSearchEvents(): boolean {
    return (
      this.stringExists(this.search) ||
      this.stringExists(this.tag) ||
      this.stringExists(this.type) ||
      (this.stringExists(this.geohash) && this.valueExists(this.radius)) ||
      this.valueExists(this.end)
    );
  }

  // helper functions for skip
  private valueExists(value: any): boolean {
    return value !== undefined && value !== null;
  }

  private stringExists(value: string | null | undefined): boolean {
    return this.valueExists(value) && (value as string).length > 0;
  }

  get searchIsUrl(): boolean {
    let url;
    if (!this.search) return false;
    try {
      url = new URL(this.search);
    } catch (_) {
      return false;
    }

    return url.protocol === "http:" || url.protocol === "https:";
  }
}
